import { ChangeDetectionStrategy, Component, inject, input, numberAttribute, OnInit, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { UnmappedNodesListComponent } from '@components/unmapped-nodes-list/unmapped-nodes-list.component';
import { CdkDropListGroup } from '@angular/cdk/drag-drop';
import { HeaderService } from '@services/header.service';
import { UserService } from '@services/user/user.service';
import { delay, distinctUntilChanged, filter, switchMap, tap } from 'rxjs';
import { NavigationService } from '@services/navigation/navigation.service';
import { CommonModule } from '@angular/common';
import { ConfirmationDialogService } from '@services/confirmation-dialog/confirmation-dialog.service';
import { AbstractUnmappedNodesDataSource, NodePoint, UnmappedNode } from '@app/shared/models/unmapped-nodes-datasource';
import { SensorNodeService } from '@services/sensor-node.service';
import { MappingService } from '@services/mapping.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EnhancedSelectable, SelectableNode } from '@app/shared/models/sensor-node';
import { HotToastService } from '@ngneat/hot-toast';
import { CancelOnlyDialogData, ConfirmDialogData } from '@components/dialogs/confirm/confirm.component';
import { DISCRIMINATOR } from '@app/shared/models/selectable.interface';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FloorplanComponent } from '@components/floorplan/floorplan.component';
import { FormBuilder } from '@angular/forms';
import { FloorplanService } from '@services/floorplan.service';

@Component({
  selector: 'app-mapping-nodes',
  templateUrl: './map-nodes.component.html',
  styleUrls: ['./map-nodes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkDropListGroup,
    CommonModule,
    FloorplanComponent,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    UnmappedNodesListComponent
  ]
})
export class MapNodesComponent implements OnInit {
  floorId = input.required<number, number | string>({ transform: numberAttribute });
  buildingId = input.required<number, number | string>({ transform: numberAttribute });

  private readonly dataSource = inject(AbstractUnmappedNodesDataSource);
  private readonly dialogService = inject(ConfirmationDialogService);
  private readonly header = inject(HeaderService);
  private readonly mappingService = inject(MappingService);
  private readonly navService = inject(NavigationService);
  private readonly snService = inject(SensorNodeService);
  private readonly toastService = inject(HotToastService);
  private readonly userService = inject(UserService);
  private readonly floorplanService = inject(FloorplanService);
  private readonly fb = inject(FormBuilder);

  private readonly MIN_REQUEST_DELAY = 750;
  readonly filterForm = this.fb.group({
    id: '',
    address: '',
    nodeType: '',
    roomName: ''
  });
  readonly nodeClicked = signal<SelectableNode | null>(null);
  readonly isRefreshButtonHidden = signal(false);
  readonly building = toSignal(
    toObservable(this.buildingId).pipe(
      filter((buildingId) => Number.isFinite(buildingId)),
      distinctUntilChanged(),
      tap(() => this.nodeClicked.set(null)),
      switchMap((buildingId) =>
        this.userService.getBuilding(buildingId).pipe(
          delay(this.MIN_REQUEST_DELAY),
          tap((building) => this.navService.initNavigation(window.location.href, building))
        )
      )
    ),
    { initialValue: undefined }
  );

  ngOnInit(): void {
    this.header.showBuildingsMenu();
    this.header.showSiteMenu();
    this.header.showUserMenu();
    this.header.showSessionMenu();
    this.header.showFloorsMenu();
  }

  onScroll(deltaY: number): void {
    this.isRefreshButtonHidden.set(deltaY > 0);
  }

  nodeDropped(target: NodePoint): void {
    const building = this.building();
    if (!building) {
      return;
    }

    const msgBoxData = new ConfirmDialogData(
      'Are you sure you want to add this node to the floor plan?',
      'Add node to floorplan'
    );
    this.dialogService.open(msgBoxData).subscribe((confirm) => {
      if (confirm) {
        const factor = this.floorplanService.getImageScalingFactor();
        target.dropPoint.x = Math.round(target.dropPoint.x / factor);
        target.dropPoint.y = Math.round(target.dropPoint.y / factor);
        this.mappingService
          .mapGenericNode(target, this.floorId())
          .pipe(
            delay(this.MIN_REQUEST_DELAY),
            this.toastService.observe({
              loading: {
                content: 'Mapping node on the floor, please wait',
                attributes: { 'data-cy': 'map-pending-toast' }
              },
              success: {
                content: `Node with the address ${target.address.toString(16)}, was successfully mapped`,
                attributes: { 'data-cy': 'map-success-toast' }
              },
              error: { content: 'Mapping failed! Please try again', attributes: { 'data-cy': 'map-error-toast' } }
            })
          )
          .subscribe({
            next: () => {
              this.snService.fetchNodes(this.floorId(), building.id);
              this.dataSource.loadUnmappedNodes(building.id, this.filterForm.getRawValue());
            },
            error: (err) => console.error(err)
          });
      }
    });
  }

  issueBlinkCommand(nodeToBlink: Pick<UnmappedNode, 'nodeId' | 'buildingId' | 'address'>): void {
    this.snService
      .blinkNode(nodeToBlink.nodeId, nodeToBlink.buildingId)
      .pipe(
        delay(this.MIN_REQUEST_DELAY),
        this.toastService.observe({
          loading: { content: 'Sending blink command to node', attributes: { 'data-cy': 'blink-pending-toast' } },
          success: {
            content: `Blink command was issued for node with the address ${nodeToBlink.address.toString(16)}`,
            attributes: { 'data-cy': 'blink-success-toast' }
          },
          error: { content: 'Blink command not sent! Please try again', attributes: { 'data-cy': 'blink-error-toast' } }
        })
      )
      .subscribe({
        error: (err) => console.error(err)
      });
  }

  refreshNodesOnFloorplan(): void {
    const building = this.building();
    if (!building) {
      return;
    }

    this.snService.fetchNodes(this.floorId(), building.id);
  }

  handleNodeClick(node: SelectableNode): void {
    this.nodeClicked.set(node);
  }

  unmapNode(context: SelectableNode): void {
    const isGroupedPNHasAPublisherOnTheFloor = this.snService.currentFloorNodes.some(
      (node: EnhancedSelectable) =>
        context.isPassiveNode &&
        context.groupId != null &&
        node.discriminator === DISCRIMINATOR.SN3 &&
        node.groupId === context.groupId
    );
    if (isGroupedPNHasAPublisherOnTheFloor) {
      this.preventUnmappingGroupedPNPublisherOnTheFloor();
    } else {
      this.unmapGenericNode(context);
    }
  }

  private preventUnmappingGroupedPNPublisherOnTheFloor(): void {
    const msgBoxData = new CancelOnlyDialogData(
      'You cannot unmap a grouped passive node when publisher is on the floor',
      'Not Allowed'
    );
    this.dialogService.open(msgBoxData).subscribe(() => {
      this.nodeClicked.set(null);
    });
  }

  private unmapGenericNode(context: SelectableNode): void {
    const building = this.building();
    if (!building) {
      return;
    }

    const msgBoxData = new ConfirmDialogData('Are you sure you want to unmap this node?', 'Unmap this node');
    this.dialogService.open(msgBoxData).subscribe((confirm) => {
      if (confirm) {
        this.mappingService
          .unmapGenericNode(context)
          .pipe(
            delay(this.MIN_REQUEST_DELAY),
            this.toastService.observe({
              loading: { content: 'Unmapping the node, please wait', attributes: { 'data-cy': 'unmap-pending-toast' } },
              success: {
                content: `Node with the address ${context.address.toString(16)} has been unmapped successfully`,
                attributes: { 'data-cy': 'unmap-success-toast' }
              },
              error: { content: 'Unmapping failed! Please try again', attributes: { 'data-cy': 'unmap-error-toast' } }
            })
          )
          .subscribe({
            next: () => {
              this.snService.fetchNodes(this.floorId(), building.id);
              this.dataSource.loadUnmappedNodes(building.id, this.filterForm.getRawValue());
            },
            error: (err) => console.error(err)
          });
      }
      this.nodeClicked.set(null);
    });
  }

  blinkNode(context: SelectableNode): void {
    const building = this.building();
    if (!building) {
      return;
    }

    const payload = {
      nodeId: context.id,
      buildingId: building.id,
      address: context.address
    };
    this.issueBlinkCommand(payload);
    this.nodeClicked.set(null);
  }

  handleFloorClick(): void {
    this.nodeClicked.set(null);
  }

  isConnected(clickedNode: SelectableNode): boolean {
    return clickedNode && clickedNode.connected;
  }
}
